import Grid from '@mui/material/Grid';
import { Autocomplete, TextField } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { ErrorFlow } from 'olympe';

export const GoodReceiverFilters = ({ data, selectedFilters, setSelectedFilters, context, getOptions }) => {
    // Constant
    const MAX_DROPDOWN_CONTENT = 500;

    // Declaring variables
    const [plants, setPlants] = useState(selectedFilters.plants || []);
    const [storageLocations, setStorageLocations] = useState(selectedFilters.storageLocations || []);
    const [materialGroups, setMaterialGroups] = useState(selectedFilters.materialGroups || []);
    const [materialTypes, setMaterialTypes] = useState(selectedFilters.materialTypes || []);
    const [plantsOptions, setPlantsOptions] = useState([]);
    const [storageLocationsOptions, setStorageLocationsOptions] = useState([]);

    // In order to avoid a too long rendering adding too much useless elements in the dropdown, we slice the collection
    // Only for those that have data collection greater than 2000 lines
    useEffect(() => {
        if (data.Plants !== undefined && plantsOptions.length === 0) {
            setPlantsOptions(data.Plants.slice(0, MAX_DROPDOWN_CONTENT));
        }
    }, [data]);

    // filtering the big collection to render only what's needed in the DOM for performances reasonsv
    const setNewPlantOptions = (v) => {
        setPlantsOptions(
            data.Plants?.filter(
                (p) =>
                    p.PlantCode.toLowerCase().includes(v.toLowerCase()) ||
                    p.PlantName.toLowerCase().includes(v.toLowerCase())
            ).slice(0, MAX_DROPDOWN_CONTENT) ?? []
        );
    };

    // Update the selected filters
    useEffect(() => {
        setSelectedFilters({
            companyCodes: selectedFilters.companyCodes,
            agents: selectedFilters.agents,
            role: selectedFilters.role,
            positions: selectedFilters.positions,
            responsibilities: selectedFilters.responsibilities,
            plants,
            storageLocations,
            materialTypes,
            materialGroups,
        });
    }, [storageLocations, plants, materialGroups, materialTypes]);

    const handlePlantChange = async (value) => {
        setPlants(value);
        setStorageLocationsOptions([]);
        setStorageLocations([]);

        if (value?.length) {
            const [controlFlow, pathInput, filtersInput] = getOptions.getInputs();

            const [successOutput, resultsOutput, totalOutput, errorOutput] = getOptions.getOutputs();

            const filter = value.map((v) => `Werks eq '${v.PlantCode}'`).join(' or ');

            const getOptionsRunner = context
                .runner(getOptions)
                .set(pathInput, 'StorageLocSet')
                .set(filtersInput, filter);

            getOptionsRunner.trigger(controlFlow);

            // eslint-disable-next-line no-unused-vars
            const [results, total] = await Promise.any([
                getOptionsRunner.waitFor(successOutput),
                getOptionsRunner.waitFor(errorOutput),
            ])
                .then((outputFlow) => {
                    if (outputFlow instanceof ErrorFlow) {
                        throw new Error(outputFlow.getMessage());
                    } else {
                        return [getOptionsRunner.get(resultsOutput), getOptionsRunner.get(totalOutput)];
                    }
                })
                .finally(() => getOptionsRunner.destroy());

            setStorageLocationsOptions(results);
        }
    };

    // Display the UI
    return (
        <Grid container spacing={2} style={{ paddingTop: '15px' }}>
            <Grid item xs={12}>
                <Autocomplete
                    id="gr-plants"
                    options={data.Plants || []}
                    getOptionLabel={(option) => option?.PlantCode + ' - ' + option?.PlantName}
                    renderInput={(params) => <TextField {...params} label="Plants" />}
                    onInputChange={(e, v) => setNewPlantOptions(v)}
                    onChange={(e, v) => handlePlantChange(v)}
                    value={plants || []}
                    fullWidth
                    multiple
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    id="gr-storage-locations"
                    options={storageLocationsOptions || []}
                    getOptionLabel={(option) => option?.Lgort + ' - ' + option?.Lgobe + ' (' + option?.Werks + ')'}
                    renderInput={(params) => <TextField {...params} label="Storage Location" />}
                    onChange={(e, v) => setStorageLocations(v)}
                    value={storageLocations || []}
                    disabled={plants.length === 0}
                    fullWidth
                    multiple
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    id="gr-material-type"
                    options={data.MaterialType || []}
                    getOptionLabel={(option) => option?.Mtart + ' - ' + option?.Mtbez}
                    renderInput={(params) => <TextField {...params} label="Material Type" />}
                    onChange={(e, v) => setMaterialTypes(v)}
                    value={materialTypes || []}
                    fullWidth
                    multiple
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    id="gr-material-group"
                    options={data.MaterialGroup || []}
                    getOptionLabel={(option) => option?.MatGroup + ' - ' + option?.MatGroupDesc}
                    renderInput={(params) => <TextField {...params} label="Material Group" />}
                    onChange={(e, v) => setMaterialGroups(v)}
                    value={materialGroups || []}
                    fullWidth
                    multiple
                />
            </Grid>
        </Grid>
    );
};
